import { useEffect, useState } from 'react';
import { MessageCircleIcon, RotateCwIcon, WifiOffIcon } from 'lucide-react';
import { salon, LOGO } from '../../data/content';
import { shopLabels } from '../../data/shopLabels';
import { Skeleton } from '../../ui/Skeleton';
import { btnPrimary, btnSecondary } from '../../ui/styles';
import { ProductGridSkeleton } from './ProductGridSkeleton';
type Props = {
  /** Foutmelding, of null tijdens het laden. */
  error: string | null;
  onRetry: () => void;
};

/**
 * Wat bezoekers zien zolang de collectie van de server komt, of als die niet bereikbaar is.
 * Laden: een skelet in de vorm van de winkel (geen leeg scherm, geen verspringing).
 * Fout: een uitleg met opnieuw proberen, en automatisch opnieuw zodra de verbinding terug is.
 */
export function CatalogFallback({
  error,
  onRetry
}: Props) {
  const [offline, setOffline] = useState(() => typeof navigator !== 'undefined' && !navigator.onLine);
  useEffect(() => {
    const goOnline = () => {
      setOffline(false);
      if (error) onRetry();
    };
    const goOffline = () => setOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [error, onRetry]);
  if (error) {
    return <main className="flex min-h-screen min-h-dvh w-full flex-col items-center justify-center bg-sand px-6 text-center" data-id="element-8585" data-mp="3t84zdmejdx9k">
        <img src={LOGO} alt={salon.name} className="h-12 w-auto mix-blend-multiply" data-id="element-8586" data-mp="6ytyxlnlx71y" />
        <div className="mt-10 max-w-sm" role="alert" data-id="element-8587" data-mp="6wzoj19yoxc4">
          {offline && <WifiOffIcon className="mx-auto mb-4 h-6 w-6 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-8588" data-mp="kppc3a552abc" />}
          <h1 className="font-display text-3xl font-light text-ink" data-id="element-8589" data-mp="gp5d01108wmk">{offline ? 'Je bent offline' : 'De winkel is even niet bereikbaar'}</h1>
          <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-8590" data-mp="ppbmzcxhvegz">
            {offline ? 'Zodra je weer verbinding hebt, laden we de collectie vanzelf opnieuw.' : `${error.replace(/[.!]+$/, '')}. Probeer het zo nog eens — of stel je vraag direct via WhatsApp.`}
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3" data-id="element-8591" data-mp="3si83ghsxj0mn">
            <button type="button" onClick={onRetry} className={btnPrimary} data-id="element-8592" data-mp="3sfvidvrvka5d">
              <RotateCwIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8593" data-mp="14vccuwhryh2" />
              Opnieuw proberen
            </button>
            <a href={salon.whatsapp} target="_blank" rel="noopener noreferrer" className={btnSecondary} data-id="element-8594" data-mp="b1gijjk8zfmh">
              <MessageCircleIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8595" data-mp="f3bjgmy9nqzq" />
              Stel je vraag
              <span className="sr-only" data-id="element-8596" data-mp="k2er8f5msbbe">{shopLabels.viaWhatsApp}</span>
            </a>
          </div>
        </div>
      </main>;
  }
  return <div className="min-h-screen min-h-dvh w-full bg-sand" data-id="element-8597" data-mp="2gwj9aqbi9qzv">
      <p role="status" className="sr-only" data-id="element-8598" data-mp="2lukrl913xmaa">
        {shopLabels.gridLoading}
      </p>
      <div className="h-9 bg-ink" aria-hidden="true" data-id="element-8599" data-mp="2eaa5z7zumv1x" />
      <header className="border-b border-line" aria-hidden="true" data-id="element-8600" data-mp="2ec3eksu0vpck">
        <div className="mx-auto flex h-20 max-w-content items-center justify-between px-6 lg:px-10" data-id="element-8601" data-mp="29iif0wcs6nb9">
          <img src={LOGO} alt="" className="h-10 w-auto mix-blend-multiply" data-id="element-8602" data-mp="24kywvlcypqe1" />
          <div className="hidden gap-7 lg:flex" data-id="element-8603" data-mp="1zlvw4d40m6bv">
            {[0, 1, 2, 3, 4].map(i => <Skeleton key={i} className="h-3 w-16" data-id="element-8604" data-mp="2x94kqgzbbvi3" />)}
          </div>
          <Skeleton className="h-10 w-10 rounded-full lg:w-32" data-id="element-8605" data-mp="2sa1hn22w7zcr" />
        </div>
      </header>
      <div className="mx-auto max-w-content px-6 pb-24 pt-10 lg:px-10" aria-hidden="true" data-id="element-8606" data-mp="2naf1okvok509">
        <Skeleton className="h-10 w-2/3 max-w-md" data-id="element-8607" data-mp="2j93j2zb84k1k" />
        <Skeleton className="mt-4 h-4 w-full max-w-lg" data-id="element-8608" data-mp="2j8bo9qpgaqcc" />
        <div className="mt-8 flex gap-2" data-id="element-8609" data-mp="1bv9yzgvju6gn">
          {[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-9 w-24 rounded-full" data-id="element-8610" data-mp="1bt7007cf52zs" />)}
        </div>
        <div className="mt-10" data-id="element-8611" data-mp="17wavm0pguhrb">
          <ProductGridSkeleton data-id="element-8612" data-mp="2vq49xkq1b6a8" />
        </div>
      </div>
    </div>;
}